import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { Guid } from 'guid-typescript';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { createProduct, Entry, Product, getEntryTotalCosts, EntyPriceInfo } from '../../shared';
import { CsSetupSection, ChargingStationEntry, ProductRelatedEntry, CustomEntry, ChargingStation } from '../charging-stations-section.model';

export interface EditableEntry extends Entry {
  readonly entryType: EntryType;
}

export interface EditableProductRelatedEntry extends EditableEntry {
  readonly latestProduct: Product;
  readonly relatedProductWasDeleted: boolean;
  readonly relatedProductWasUpdated: boolean;
  product: Product;
}

export enum EntryType {
  ChargingStation,
  CsOptionalFeature,
  CsCustomFeature
}

export enum RelatedProductsState {
  AllUpToDate,
  AnyOutdated,
  AnyDeleted,
  ChargingStationDeleted
}

export enum ProductRelatedEntryState {
  ProductUpToDate,
  ProductOutdated,
  ProductDeleted
}

export class EditableCsSetupSection {
  getEntryTotalCosts = getEntryTotalCosts;
  readonly entries$: Observable<EditableEntry[]>;
  private readonly totalCostsSubject$: BehaviorSubject<number>;
  private readonly totalCostsObservable$: Observable<number>;
  private readonly displayProductUpdatesSubject$: BehaviorSubject<boolean>;
  private chargingStations: ReadonlyArray<ChargingStation>;
  private entriesSubject$: BehaviorSubject<EditableEntry[]>;
  private csSetupSection: CsSetupSection;
  private internalRelatedProductsState: RelatedProductsState;
  private internalSelectedChargingStation: ChargingStation;

  public get relatedProductsState(): RelatedProductsState {
    return this.internalRelatedProductsState;
  }

  public get allProductsUpToDate(): boolean {
    return this.relatedProductsState === RelatedProductsState.AllUpToDate;
  }

  public get containsOutdatedProduct(): boolean {
    return this.relatedProductsState === RelatedProductsState.AnyOutdated;
  }

  public get containsDeletedProduct(): boolean {
    return this.relatedProductsState === RelatedProductsState.AnyDeleted;
  }

  public get containsDeletedChargingStation(): boolean {
    return this.relatedProductsState === RelatedProductsState.ChargingStationDeleted;
  }

  public get csQuantity(): number {
    return this.getChargingStationEntry(this.currentEntries).quantity;
  }

  public set csQuantity(newQuantity: number) {
    this.getChargingStationEntry(this.currentEntries).quantity = newQuantity;
  }

  public get totalCosts(): number {
    const totalCosts = this.calculateTotalCosts();
    // This is the only place where change of totalCosts can be detected.
    this.totalCostsSubject$.next(totalCosts);
    return totalCosts;
  }

  public get totalCosts$(): Observable<number> {
    return this.totalCostsObservable$;
  }

  public get selectedChargingStation(): ChargingStation {
    return this.internalSelectedChargingStation;
  }

  public get hasOptionalFeatures(): boolean {
    return this.selectedChargingStation.optionalFeatures.length > 0;
  }

  public get entryTypes(): any {
    return EntryType;
  }

  public get displayProductUpdates$(): Observable<boolean> {
    return this.displayProductUpdatesSubject$
      .asObservable()
      .pipe(distinctUntilChanged());
  }

  public get displayProductUpdates(): boolean {
    return this.displayProductUpdatesSubject$.value;
  }

  private get currentEntries(): EditableEntry[] {
    return this.entriesSubject$.getValue();
  }

  private constructor(csSetupSection: CsSetupSection,
                      entries: EditableEntry[],
                      chargingStations: ReadonlyArray<ChargingStation>)
  {
    this.csSetupSection = csSetupSection;
    this.chargingStations = chargingStations;

    this.entriesSubject$ = new BehaviorSubject<EditableEntry[]>(entries);
    this.entries$ = this.entriesSubject$.asObservable();

    this.totalCostsSubject$ = new BehaviorSubject<number>(0);
    this.totalCostsObservable$ = this.totalCostsSubject$
      .asObservable()
      .pipe(distinctUntilChanged());

    this.displayProductUpdatesSubject$ = new BehaviorSubject<boolean>(false);

    const chargingStationEntry = this.getChargingStationEntry(entries);
    const chargingStation = this.chargingStations.find(cs => cs.id === chargingStationEntry.product.id);
    this.updateSelectedChargingStation(chargingStation);
    this.updateRelatedProductsState(entries, chargingStation);
    this.updateTotalCosts();
  }

  static createFromCsSetupSection(csSetupSection: CsSetupSection, chargingStations: ReadonlyArray<ChargingStation>)
  : EditableCsSetupSection {
    const chargingStation = chargingStations.find(cs => cs.id === csSetupSection.chargingStationEntry.productId);
    const newEntries = createEditableEntries(
      chargingStation,
      csSetupSection.chargingStationEntry,
      csSetupSection.productRelatedEntries,
      csSetupSection.customEntries);
    return new EditableCsSetupSection(
      csSetupSection,
      newEntries,
      chargingStations);
  }

  getCsSetupSection(): CsSetupSection {
    const editableCsEntry = this.getChargingStationEntry(this.currentEntries);
    return {
      id: this.csSetupSection.id,
      createdOn: this.csSetupSection.createdOn,
      chargingStationEntry: createChargingStationEntry(this.csSetupSection.id, editableCsEntry),
      productRelatedEntries: createOptionalFeatureEntries(this.csSetupSection.id, this.currentEntries),
      customEntries: createCustomFeatureEntries(this.csSetupSection.id, this.currentEntries)
    };
  }

  onChargingStationSelected(chargingStationEntry: EditableChargingStation, selectedChargingStationId): void {
    this.entriesSubject$.next([ chargingStationEntry ]);
    const chargingStation = this.chargingStations.find(p => p.id === selectedChargingStationId);
    chargingStationEntry.product = chargingStation;
    this.updateSelectedChargingStation(chargingStation);
  }

  onOptionalFeatureSelected(editableOptionalFeature, selectedOptionalFeatureId: number): void {
    editableOptionalFeature.product = this.selectedChargingStation.optionalFeatures.find(p => p.id === selectedOptionalFeatureId);
  }

  onDisplayProductUpdatesChanged(displayUpdatedProducts: boolean): void {
    this.updateTotalCosts();
    this.displayProductUpdatesSubject$.next(displayUpdatedProducts);
  }

  onDrop(event: CdkDragDrop<string[]>): void {
    moveItemInArray(this.currentEntries, event.previousIndex, event.currentIndex);
    for (let i = 1; i < this.currentEntries.length; i++) {
      this.currentEntries[i].positionNumber = i;
    }

    this.entriesSubject$.next(this.currentEntries);
  }

  /**
   * Predicate function that disallows the first row to be moved 
   * and other rows to be inserted as the first row by drag and drop.
   */
   sortPredicate(index: number, drag: CdkDrag, drop: CdkDropList): boolean {
    return index !== 0;
  }

  reset(): void {
    const chargingStation = this.chargingStations.find(cs => cs.id === this.csSetupSection.chargingStationEntry.productId);
    const editableEntries = createEditableEntries(
      chargingStation,
      this.csSetupSection.chargingStationEntry,
      this.csSetupSection.productRelatedEntries,
      this.csSetupSection.customEntries);
    this.entriesSubject$.next(editableEntries);
    this.updateSelectedChargingStation(chargingStation);
    this.updateRelatedProductsState(editableEntries, chargingStation);
  }

  applyChanges(): void {
    this.csSetupSection = this.getCsSetupSection();
    const chargingStation = this.chargingStations.find(cs => cs.id === this.csSetupSection.chargingStationEntry.productId);
    const editableEntries = createEditableEntries(
      chargingStation,
      this.csSetupSection.chargingStationEntry,
      this.csSetupSection.productRelatedEntries,
      this.csSetupSection.customEntries);
    this.entriesSubject$.next(editableEntries);
    this.updateRelatedProductsState(this.currentEntries, this.selectedChargingStation);
  }

  addOptionalFeature(): void {
    const newEntry = new EditableCsFeature(
      Guid.create(),
      new Date(),
      this.currentEntries.length,
      this.selectedChargingStation?.optionalFeatures[0],
      0,
      false,
      this.selectedChargingStation?.optionalFeatures[0]
    );
    this.currentEntries.push(newEntry);
    this.entriesSubject$.next(this.currentEntries);
  }

  addCustomEntry(): void {
    const newEntry = new EditableCsCustomFeature(
      Guid.create(),
      new Date(),
      this.currentEntries.length,
      'Benutzerdefinierte Position',
      0,
      this.csQuantity,
      0,
      false
    );
    this.currentEntries.push(newEntry);
    this.entriesSubject$.next(this.currentEntries);
  }

  removeEntry(entryToDelete: EditableEntry): void {
    const index = this.currentEntries.indexOf(entryToDelete, 0);
    if (index < 0) {
      return;
    }
    this.currentEntries.splice(index, 1);
    this.entriesSubject$.next(this.currentEntries);
  }

  alignEntriesToCurrentProducts(): void {
    const productRelatedEntries = this.currentEntries
      .filter(e => e.entryType === EntryType.ChargingStation || e.entryType === EntryType.CsOptionalFeature)
      .map(e => e as EditableProductRelatedEntry);

    productRelatedEntries
      .filter(e => !e.latestProduct)
      .forEach(entryWithDeletedProduct => {
        const index = this.currentEntries.findIndex(e => e.id === entryWithDeletedProduct.id);
        this.currentEntries.splice(index, 1);
      });

    productRelatedEntries
      .filter(e => e.latestProduct && e.product.version < e.latestProduct.version)
      .forEach(entryWithOutdatedProduct => {
        entryWithOutdatedProduct.product = entryWithOutdatedProduct.latestProduct;
      });

    this.entriesSubject$.next(this.currentEntries);
    this.displayProductUpdatesSubject$.next(false);
  }

  isAlreadyPartOfCsSetup(productId: number): boolean {
    return this.currentEntries
      .filter(e => e.entryType === EntryType.CsOptionalFeature)
      .map(e => e as EditableProductRelatedEntry)
      .some(e => e.product.id === productId);
  }

  private getChargingStationEntry(editableEntries: EditableEntry[]): EditableChargingStation {
    const csItem = editableEntries.find(e => e.entryType === EntryType.ChargingStation);
    return (csItem as EditableChargingStation);
  }

  private updateRelatedProductsState(editableEntries: EditableEntry[], chargingStation: ChargingStation): void{
    const productRelatedEntries = editableEntries
      .filter(e =>
        e.entryType === EntryType.ChargingStation ||
        e.entryType === EntryType.CsOptionalFeature)
      .map(e => e as EditableProductRelatedEntry);
    this.internalRelatedProductsState = this.getRelatedProductsState(productRelatedEntries);
  }

  private updateSelectedChargingStation(selectedChargingStation: ChargingStation): void{
    this.internalSelectedChargingStation = selectedChargingStation;
  }

  private updateTotalCosts(): void {
    const totalCosts = this.calculateTotalCosts();
    this.totalCostsSubject$.next(totalCosts);
  }

  private getRelatedProductsState(entries: EditableProductRelatedEntry[])
    : RelatedProductsState {
    const chargingStationDeleted = entries
      .find(e => e.entryType === EntryType.ChargingStation && !e.latestProduct);
    return chargingStationDeleted
      ? RelatedProductsState.ChargingStationDeleted
      : entries.some(e => !e.latestProduct)
        ? RelatedProductsState.AnyDeleted
        : entries.some(e => e.product.version < e.latestProduct.version)
          ? RelatedProductsState.AnyOutdated
          : RelatedProductsState.AllUpToDate;
  }

  private calculateTotalCosts(): number {
    if (this.displayProductUpdates && this.relatedProductsState === RelatedProductsState.ChargingStationDeleted) {
      return 0;
    }

    const customFeaturesTotalCosts = this.currentEntries
      .filter(e => e.entryType === EntryType.CsCustomFeature)
      .map(e => e as EditableCsCustomFeature)
      .reduce((a, u) => a + getEntryTotalCosts(u, u.quantity), 0);
    const csAndOptionalFeaturesTotalCosts = this.currentEntries
      .filter(e => e.entryType !== EntryType.CsCustomFeature)
      .reduce((a, u) => a + (u as any).getTotalCosts(this.displayProductUpdates, this.csQuantity), 0);
    return customFeaturesTotalCosts + csAndOptionalFeaturesTotalCosts;
  }
}



class EditableChargingStation implements EditableProductRelatedEntry {
  readonly id: Guid;
  readonly createdOn: Date;
  readonly entryType: EntryType;
  readonly latestProduct: Product;
  product: Product;
  quantity: number;
  discountFactor: number;
  isAlternative: boolean;
  positionNumber: number;

  public get relatedProductWasDeleted(): boolean {
    return this.state === ProductRelatedEntryState.ProductDeleted;
  }

  public get relatedProductWasUpdated(): boolean {
    return this.state === ProductRelatedEntryState.ProductOutdated;
  }

  public get pricePerUnit(): number {
    return this.product.pricePerUnit;
  }

  public get state(): ProductRelatedEntryState {
    return !this.latestProduct
      ? ProductRelatedEntryState.ProductDeleted
      : this.latestProduct.version > this.product.version
        ? ProductRelatedEntryState.ProductOutdated
        : ProductRelatedEntryState.ProductUpToDate;
  }

  constructor(
    id: Guid,
    createdOn: Date,
    chargingStation: Product,
    quantity: number,
    discountFactor: number,
    isAlternative: boolean,
    latestProduct: Product) {
    this.id = id;
    this.createdOn = createdOn;
    this.positionNumber = -1;
    this.entryType = EntryType.ChargingStation;
    this.latestProduct = latestProduct;
    this.product = chargingStation;
    this.quantity = quantity;
    this.discountFactor = discountFactor;
    this.isAlternative = isAlternative;
  }

  getPricePerUnit(useLatestPrice: boolean): number {
    return useLatestPrice && this.state === ProductRelatedEntryState.ProductOutdated
      ? this.latestProduct.pricePerUnit
      : this.product.pricePerUnit;
  }

  getTotalCosts(useLatestPrice: boolean, quantity: number): number {
    const priceInfo = this.getPriceInfo(useLatestPrice);
    return getEntryTotalCosts(priceInfo, quantity);
  }

  private getPriceInfo(useLatestPrice: boolean): EntyPriceInfo {
    const pricePerUnit = useLatestPrice
      ? this.state === ProductRelatedEntryState.ProductDeleted
        ? 0
        : this.latestProduct.pricePerUnit
      : this.product.pricePerUnit;
    return {
      pricePerUnit,
      isAlternative: this.isAlternative,
      discountFactor: this.discountFactor };
  }
}

class EditableCsFeature implements EditableProductRelatedEntry {
  readonly id: Guid;
  readonly createdOn: Date;
  readonly entryType: EntryType;
  readonly latestProduct: Product;
  product: Product;
  discountFactor: number;
  isAlternative: boolean;
  positionNumber: number;

  public get relatedProductWasDeleted(): boolean {
    return this.state === ProductRelatedEntryState.ProductDeleted;
  }

  public get relatedProductWasUpdated(): boolean {
    return this.state === ProductRelatedEntryState.ProductOutdated;
  }

  public get pricePerUnit(): number {
    return this.product.pricePerUnit;
  }

  public get state(): ProductRelatedEntryState {
    return !this.latestProduct
      ? ProductRelatedEntryState.ProductDeleted
      : this.latestProduct.version > this.product.version
        ? ProductRelatedEntryState.ProductOutdated
        : ProductRelatedEntryState.ProductUpToDate;
  }

  constructor(
    id: Guid,
    createdOn: Date,
    positionNumber: number,
    product: Product,
    discountFactor: number,
    isAlternative: boolean,
    latestProduct: Product) {
    this.id = id;
    this.createdOn = createdOn;
    this.positionNumber = positionNumber;
    this.entryType = EntryType.CsOptionalFeature;
    this.product = product;
    this.discountFactor = discountFactor;
    this.isAlternative = isAlternative;
    this.latestProduct = latestProduct;
  }

  getPricePerUnit(useLatestPrice: boolean): number {
    return useLatestPrice && this.state === ProductRelatedEntryState.ProductOutdated
      ? this.latestProduct.pricePerUnit
      : this.product.pricePerUnit;
  }

  getTotalCosts(useLatestPrice: boolean, quantity: number): number {
    const priceInfo = this.getPriceInfo(useLatestPrice);
    return getEntryTotalCosts(priceInfo, quantity);
  }

  private getPriceInfo(useLatestPrice: boolean): EntyPriceInfo {
    const pricePerUnit = useLatestPrice
      ? this.state === ProductRelatedEntryState.ProductDeleted
        ? 0
        : this.latestProduct.pricePerUnit
      : this.product.pricePerUnit;
    return {
      pricePerUnit,
      isAlternative: this.isAlternative,
      discountFactor: this.discountFactor };
  }
}

class EditableCsCustomFeature implements EditableEntry {
  readonly id: Guid;
  readonly createdOn: Date;
  readonly entryType: EntryType;
  description: string;
  pricePerUnit: number;
  quantity: number;
  discountFactor: number;
  isAlternative: boolean;
  positionNumber: number;

  constructor(
    id: Guid,
    createdOn: Date,
    positionNumber: number,
    description: string,
    pricePerUnit: number,
    quantity: number,
    discountFactor: number,
    isAlternative: boolean) {
    this.id = id;
    this.createdOn = createdOn;
    this.positionNumber = positionNumber;
    this.entryType = EntryType.CsCustomFeature;
    this.description = description;
    this.pricePerUnit = pricePerUnit;
    this.quantity = quantity;
    this.discountFactor = discountFactor;
    this.isAlternative = isAlternative;
  }

  static createFromCustomEntry(customEntry: CustomEntry): EditableCsCustomFeature {
    return new EditableCsCustomFeature(
      customEntry.id,
      customEntry.createdOn,
      customEntry.positionNumber,
      customEntry.description,
      customEntry.pricePerUnit,
      customEntry.quantity,
      customEntry.discountFactor,
      customEntry.isAlternative,
    );
  }
}

function createEditableEntries(
  chargingStation: ChargingStation,
  chargingStationEntry: ChargingStationEntry,
  productEntries: ProductRelatedEntry[],
  customEntries: CustomEntry[]): EditableEntry[]{
    const editableEntries: EditableEntry[] = [];
    const editableChargingStation = new EditableChargingStation(
      chargingStationEntry.id,
      chargingStationEntry.createdOn,
      createProduct(chargingStationEntry),
      chargingStationEntry.quantity,
      chargingStationEntry.discountFactor,
      chargingStationEntry.isAlternative,
      chargingStation);

    const editableCsFeatures = createEditableCsFeatures(productEntries, chargingStation?.optionalFeatures);
    const editableCsCustomFeatures = createEditableCsCustomFeatures(customEntries);
    editableEntries.push(editableChargingStation);
    return editableEntries
      .concat(editableCsFeatures)
      .concat(editableCsCustomFeatures)
      .sort((a, b) => a.positionNumber - b.positionNumber);
}

function createEditableCsFeatures(
  csFeatureEntries: ProductRelatedEntry[],
  csFeatures: Product[])
  : EditableCsFeature[]
{
  const editableCsFeatures: EditableCsFeature[] = [];
  csFeatureEntries.forEach(entry => {
    const optionalFeature = createProduct(entry);
    const latestProduct = csFeatures
      ? findProduct(entry.productId, csFeatures)
      : undefined;
    editableCsFeatures.push(
      new EditableCsFeature(entry.id, entry.createdOn, entry.positionNumber, optionalFeature, entry.discountFactor, entry.isAlternative, latestProduct));
  });
  return editableCsFeatures;
}

function createEditableCsCustomFeatures(csCustomFeatures: CustomEntry[]): EditableCsCustomFeature[] {
  const editableCsCustomFeatures: EditableCsCustomFeature[] = [];
  csCustomFeatures.forEach(entry => {
    editableCsCustomFeatures.push(EditableCsCustomFeature.createFromCustomEntry(entry));
  });
  return editableCsCustomFeatures;
}

function createChargingStationEntry(sectionId: Guid, editableEntry: EditableChargingStation): ChargingStationEntry {
  return {
    ...editableEntry,
    sectionId,
    productId: editableEntry.product.id,
    productVersion: editableEntry.product.version,
    description: editableEntry.product.name,
    pricePerUnit: editableEntry.product.pricePerUnit,
    positionNumber: -1
  };
}

function createOptionalFeatureEntries(sectionId: Guid, editableEntries: EditableEntry[]): ProductRelatedEntry[]{
  return editableEntries
    .filter(e => e.entryType === EntryType.CsOptionalFeature)
    .map(entry => entry as EditableCsFeature)
    .map(entry => ({
      ...entry,
      sectionId,
      productId: entry.product.id,
      productVersion: entry.product.version,
      description: entry.product.name,
      pricePerUnit: entry.product.pricePerUnit
    }));
}

function createCustomFeatureEntries(sectionId: Guid, editableEntries: EditableEntry[]): CustomEntry[]{
  return editableEntries
    .filter(e => e.entryType === EntryType.CsCustomFeature)
    .map(entry => ({...entry as EditableCsCustomFeature, sectionId}));
}

function findProduct(productId: number, products: Product[]): Product {
  const index = products.findIndex(p => p.id === productId);
  return index > -1
    ? products[index]
    : undefined;
}
